import axios from 'axios';
import { DOCUMENT_VERSIONS } from '../constants/documentVersions';

export interface DocumentAcceptance {
  terms_accepted_version: string;
  privacy_accepted_version: string;
  terms_accepted_at: string;
  privacy_accepted_at: string;
}

type GetTokenFn = (options?: any) => Promise<string>;

/** Match `Auth0Provider` in index.tsx — do not omit `offline_access` or scopes drift and silent auth can break. */
export const ACCESS_TOKEN_SCOPE =
  'openid profile email read:organizations roles offline_access';

export function silentTokenParams(cacheMode?: 'off') {
  const base = {
    authorizationParams: {
      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
      scope: ACCESS_TOKEN_SCOPE,
    },
  };
  return cacheMode ? { ...base, cacheMode } : base;
}

function isAuth0SilentTokenFailure(error: any): boolean {
  const msg = `${error?.message || error?.error_description || error?.error || ''}`;
  return /missing refresh token|login required|consent required|missing_refresh_token/i.test(msg);
}

export function formatTokenFailureMessage(error: any): string | null {
  if (!isAuth0SilentTokenFailure(error)) return null;
  if (/missing refresh token|missing_refresh_token/i.test(`${error?.message || ''}`)) {
    return (
      'This browser did not store a refresh token (common in private/incognito mode or strict cookie settings). ' +
      'Use a normal window, allow cookies for this site, or sign out and sign in again.'
    );
  }
  return 'Please sign out and sign in again to refresh your session.';
}

/** Prefer cached access token; avoid cacheMode: 'off' by default — it forces refresh-token flow and fails without an RT. */
export async function getAccessTokenForApi(
  getAccessTokenSilently: GetTokenFn,
  forceRefresh: boolean = false
): Promise<string> {
  const opts = silentTokenParams(forceRefresh ? 'off' : undefined) as any;
  try {
    return await getAccessTokenSilently(opts);
  } catch (err) {
    if (forceRefresh) throw err;
    try {
      return await getAccessTokenSilently();
    } catch {
      throw err;
    }
  }
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

function isRetryableAcceptanceError(error: any): boolean {
  if (isAuth0SilentTokenFailure(error)) return false;
  if (!error?.response) return true;
  const status = error.response.status as number;
  return (
    status === 401 ||
    status === 408 ||
    status === 429 ||
    status === 500 ||
    status === 502 ||
    status === 503 ||
    status === 504
  );
}

function formatAcceptanceError(error: any): string {
  const tokenMsg = formatTokenFailureMessage(error);
  if (tokenMsg) return tokenMsg;

  const status = error?.response?.status as number | undefined;
  const detail = error?.response?.data?.detail;

  if (status === 401) {
    return 'Your session may have expired. Click Accept again, or use Sign out below and log back in.';
  }
  if (status === 503) {
    return 'The service is temporarily unable to save your acceptance. Please try again in a moment.';
  }
  if (typeof detail === 'string' && detail.trim()) {
    return `${detail} If this keeps happening, sign out and sign back in.`;
  }
  return 'We could not save your acceptance right now. Click Accept again, or sign out and sign back in.';
}

export const updateDocumentAcceptance = async (
  getAccessTokenSilently: GetTokenFn,
  acceptedTerms: boolean,
  acceptedPrivacy: boolean
): Promise<void> => {
  const currentDate = new Date().toISOString();

  const updateData: Partial<DocumentAcceptance> = {};

  if (acceptedTerms) {
    updateData.terms_accepted_version = DOCUMENT_VERSIONS.terms;
    updateData.terms_accepted_at = currentDate;
  }

  if (acceptedPrivacy) {
    updateData.privacy_accepted_version = DOCUMENT_VERSIONS.privacy;
    updateData.privacy_accepted_at = currentDate;
  }

  const maxAttempts = 4;
  let lastError: any;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const token = await getAccessTokenForApi(getAccessTokenSilently, false);
      const post = (accessToken: string) =>
        axios.post(
          `${process.env.REACT_APP_API_URL}/api/auth0/update-user-metadata`,
          updateData,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            timeout: 45_000,
          }
        );

      try {
        await post(token);
      } catch (axiosErr: any) {
        if (axios.isAxiosError(axiosErr) && axiosErr.response?.status === 401) {
          const fresh = await getAccessTokenForApi(getAccessTokenSilently, true);
          await post(fresh);
        } else {
          throw axiosErr;
        }
      }
      return;
    } catch (error: any) {
      lastError = error;
      console.error('Failed to update document acceptance:', error);
      const retry = attempt < maxAttempts - 1 && isRetryableAcceptanceError(error);
      if (retry) {
        await sleep(400 * 2 ** attempt);
        continue;
      }
      throw new Error(formatAcceptanceError(error));
    }
  }

  throw new Error(formatAcceptanceError(lastError));
};

export const checkDocumentAcceptance = (
  user: any
): {
  needsTermsAcceptance: boolean;
  needsPrivacyAcceptance: boolean;
  needsAnyAcceptance: boolean;
} => {
  if (!user) {
    return {
      needsTermsAcceptance: true,
      needsPrivacyAcceptance: true,
      needsAnyAcceptance: true,
    };
  }

  // Prefer namespaced app_metadata claim; fallback to old user_metadata for backwards-compatibility
  const appMetaNs = user['https://bespot.com/app_metadata'] || {};
  const userMetaNs = user['https://bespot.com/user_metadata'] || {};
  const userMetadata = Object.keys(appMetaNs).length ? appMetaNs : userMetaNs;
  const currentTermsVersion = DOCUMENT_VERSIONS.terms;
  const currentPrivacyVersion = DOCUMENT_VERSIONS.privacy;

  const userTermsVersion = userMetadata.terms_accepted_version;
  const userPrivacyVersion = userMetadata.privacy_accepted_version;

  const needsTermsAcceptance = userTermsVersion !== currentTermsVersion;
  const needsPrivacyAcceptance = userPrivacyVersion !== currentPrivacyVersion;

  // Debug logging removed

  return {
    needsTermsAcceptance,
    needsPrivacyAcceptance,
    needsAnyAcceptance: needsTermsAcceptance || needsPrivacyAcceptance,
  };
};

// Async fallback: if ID token lacks namespaced app_metadata, try reading it from the access token
export const checkDocumentAcceptanceAsync = async (
  user: any,
  getAccessTokenSilently: (() => Promise<string>) | null
): Promise<{
  needsTermsAcceptance: boolean;
  needsPrivacyAcceptance: boolean;
  needsAnyAcceptance: boolean;
}> => {
  const base = checkDocumentAcceptance(user);
  if (!base.needsAnyAcceptance) return base;
  if (!getAccessTokenSilently) return base;

  try {
    const token = await getAccessTokenSilently();
    const parts = token.split('.');
    if (parts.length !== 3) return base;
    const payload = JSON.parse(atob(parts[1]));
    const appMetaNs = payload['https://bespot.com/app_metadata'] || {};
    const userMetaNs = payload['https://bespot.com/user_metadata'] || {};
    const userMetadata = Object.keys(appMetaNs).length ? appMetaNs : userMetaNs;

    const currentTermsVersion = DOCUMENT_VERSIONS.terms;
    const currentPrivacyVersion = DOCUMENT_VERSIONS.privacy;

    const userTermsVersion = userMetadata.terms_accepted_version;
    const userPrivacyVersion = userMetadata.privacy_accepted_version;

    const needsTermsAcceptance = userTermsVersion !== currentTermsVersion;
    const needsPrivacyAcceptance = userPrivacyVersion !== currentPrivacyVersion;

    // Debug logging removed

    return {
      needsTermsAcceptance,
      needsPrivacyAcceptance,
      needsAnyAcceptance: needsTermsAcceptance || needsPrivacyAcceptance,
    };
  } catch {
    return base;
  }
};

const DOC_ACCEPTANCE_DEFER_PREFIX = 'docAcceptanceDeferred:';

export function getDocumentAcceptanceDeferKey(userSub: string): string {
  return `${DOC_ACCEPTANCE_DEFER_PREFIX}${userSub}:${DOCUMENT_VERSIONS.terms}-${DOCUMENT_VERSIONS.privacy}`;
}

/** User chose to continue without saving; prompt again after next sign-in (defer cleared on logout). */
export function isDocumentAcceptanceDeferred(userSub: string | undefined): boolean {
  if (!userSub) return false;
  try {
    return localStorage.getItem(getDocumentAcceptanceDeferKey(userSub)) === '1';
  } catch {
    return false;
  }
}

export function setDocumentAcceptanceDeferred(userSub: string): void {
  try {
    localStorage.setItem(getDocumentAcceptanceDeferKey(userSub), '1');
  } catch {
    /* ignore */
  }
}

export function clearAllDocumentAcceptanceDeferrals(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith(DOC_ACCEPTANCE_DEFER_PREFIX)) {
        localStorage.removeItem(k);
      }
    }
  } catch {
    /* ignore */
  }
}
